import React from 'react';
import styled from 'styled-components';
import { Body5, Container, H5 } from '@ui';
import { FlatButton } from '@ui/buttons';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';
import {
  CellsEditor,
  ReplaceTemplate,
  TEMPLATE_ACTION_OPTIONS,
  TemplateDownloader,
  TemplateMetadataEditor,
} from '@features/rfp/templateActions';

const Wrapper = styled.div``;
const AvailableTemplatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TemplateSelectWrapper = styled.div`
  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    padding: 12px 16px;
  }
`;
const getTemplateName = (template) => {
  return template?.name || template?.filename || `Template ${template?.id || ''}`.trim();
};

export function EditTemplateTool({ onGoBack, modeId, tool }) {
  const [mode, setMode] = React.useState(null);
  const [availableTemplates, setAvailableTemplates] = React.useState([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState('');
  const [formError, setFormError] = React.useState(null);

  const handleGoBack = () => {
    if (mode !== null) {
      setMode(null);
      return;
    }

    if (selectedTemplate !== null) {
      setMode(null);
      setSelectedTemplate(null);
      setSelectedTemplateId('');
      return;
    }

    onGoBack();
  };

  const refreshTemplates = React.useCallback(async () => {
    const url = 'v1/genai/templates';
    setFormError(null);
    const { status, data, error } = await sendApiRequest('get', url);
    handle400Statuses(status);
    logNetRequest(url, status, data, error);

    if (status === 200) {
      setAvailableTemplates(Array.isArray(data) ? data : data?.templates || []);
      return;
    }

    setFormError(error || 'Something went wrong, try again');
  }, []);

  React.useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  React.useEffect(() => {
    if (!selectedTemplateId) {
      setSelectedTemplate(null);
      setMode(null);
      return;
    }

    const template = availableTemplates.find(
      (currentTemplate) => String(currentTemplate.id) === String(selectedTemplateId)
    );
    if (template) {
      setSelectedTemplate(template);
    } else {
      setSelectedTemplate(null);
      setSelectedTemplateId('');
      setMode(null);
    }
  }, [availableTemplates, selectedTemplateId]);

  const handleSelectedTemplateChange = (updatedTemplate) => {
    setSelectedTemplate(updatedTemplate);
    setAvailableTemplates((currentTemplates) =>
      currentTemplates.map((template) => (template.id === updatedTemplate.id ? updatedTemplate : template))
    );
  };

  const handleCancelAction = () => {
    setMode(null);
  };

  return (
    <Wrapper>
      <BackArrowButton onClick={handleGoBack} />
      <Container top={40}>
        <H5>Edit Template</H5>
        <Container top={16}>
          {formError && (
            <Container bottom={16}>
              <Body5>{formError}</Body5>
            </Container>
          )}
          <AvailableTemplatesList>
            {selectedTemplateId === '' ? (
              <TemplateSelectWrapper>
                <Container top={24}>
                  <Body5 bold>Select Template</Body5>
                </Container>
                <Container top={16}>
                  <select onChange={(e) => setSelectedTemplateId(e.target.value)} value={selectedTemplateId}>
                    <option value="">Please select a template</option>
                    {availableTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {getTemplateName(template)}
                      </option>
                    ))}
                  </select>
                </Container>
              </TemplateSelectWrapper>
            ) : null}
          </AvailableTemplatesList>

          {selectedTemplate !== null ? (
            <Container top={24}>
              <Body5 bold>Template</Body5>
              <Container top={16}>
                <Body5>{getTemplateName(selectedTemplate)}</Body5>
              </Container>
              {mode === null && (
                <Container top={40}>
                  <Body5 bold>Please select an action</Body5>
                  {TEMPLATE_ACTION_OPTIONS.map((action, index) => (
                    <Container top={index === 0 ? 24 : 16} key={action.id}>
                      <FlatButton fullWidth onClick={() => setMode(action.id)}>
                        {action.label}
                      </FlatButton>
                    </Container>
                  ))}
                  <Container top={24} />
                </Container>
              )}
              {mode === 'edit_template' && (
                <Container top={16}>
                  <TemplateMetadataEditor
                    template={selectedTemplate}
                    onTemplateChange={handleSelectedTemplateChange}
                    onCancel={handleCancelAction}
                  />
                </Container>
              )}
              {mode === 'edit_cells' && (
                <Container top={16}>
                  <CellsEditor template={selectedTemplate} onCancel={handleCancelAction} />
                </Container>
              )}
              {mode === 'download_template' && (
                <Container top={16}>
                  <TemplateDownloader template={selectedTemplate} onCancel={handleCancelAction} />
                </Container>
              )}
              {mode === 'replace_template' && (
                <Container top={16}>
                  <ReplaceTemplate
                    template={selectedTemplate}
                    onCancel={handleCancelAction}
                    onUploadComplete={refreshTemplates}
                  />
                </Container>
              )}
            </Container>
          ) : (
            <div style={{ height: '100px' }} />
          )}
        </Container>
      </Container>
    </Wrapper>
  );
}
